import { getMobileOS } from "@/shared/utils/os_logic";
import {
  ErrorDialogContext,
  errorHandledAsync,
} from "@/shared/context/error-context";
import { ClientSystemError, MoneyLinkException } from "@/shared/utils/custom-exception";
import { BaseRoundButton } from "@/shared/components/button/base-round-button";
import React, { Dispatch, SetStateAction, useContext, useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";
import { useBreakPoint } from "@/shared/config/use_break_point";
// import { Const, Mode } from "../common/const";
import { OcrCameraAggregateMode } from "@/shared/config/globals"
import { OcrCameraAggregateModeType } from "@/shared/config/globals";

interface Props {
  changeStatus: (mode: OcrCameraAggregateModeType) => void;
  addImageList: (image: string) => void;
  selectedFiles: File[];
  setSelectedFiles: Dispatch<SetStateAction<File[]>>;
}

export const Camera = (props: Props) => {
  const {
    changeStatus,
    addImageList,
  } = props;

  // 現在のモーダルサイズを取得
  const getModalDimensions = () => {
    const el = document.getElementsByClassName("modal-content")[0] as HTMLElement | undefined;
  if (!el) {
    return { width: 0, height: 0 };
  }
  return {
    width: el.clientWidth,
    height: el.clientHeight,
  };
  };

  // 現在の画面サイズを取得
  const getWindowDimensions = () => {
    return {
      width: window.innerWidth,
      height: window.innerHeight,
    };
  };

  // カメラ領域の計算（現状全て適当な値）
  const getRegionSize = () => {
    // const windowSize = getModalDimensions();
    const windowSize = getWindowDimensions();
    const isPortrait = getOrientation() === "portrait";
    const os = getMobileOS();
    if (os === "iPhone" || os === "Android") {
      // Note: INVBG-152にてuserAgentを修正したが、カメラ領域に関してはコードを精査してから修正する必要があるため、今までと同様の状態になるようにiPadはこの分岐に含めていない
      if (isPortrait) {
        return {
          width: windowSize.width * (85 / 100.0),
          height: windowSize.height * (65 / 100.0),
        };
      } else {
        return {
          width: windowSize.width * (65 / 100.0),
          height: windowSize.height * (85 / 100.0)
        };
      }
    }
    return {
      width: windowSize.width * (85 / 100.0),
      height: windowSize.height * (65 / 100.0),
    };
  };

  // 画面の向き取得
  const getOrientation = () => {
    let orientation = "unknown";
    if (window.matchMedia("(orientation: portrait)").matches) {
      orientation = "portrait";
    }
    if (window.matchMedia("(orientation: landscape)").matches) {
      orientation = "landscape";
    }
    return orientation;
  };

  // カメラサイズ計算
  const calcCameraSize = () => {
    // モバイルの場合、Portraitだとカメラ設定が逆転するのでその対応
    const isPortrait = getOrientation() === "portrait";
    const regionSize = getRegionSize();
    // モバイルで表示している場合反転させる
    const os = getMobileOS();
    if (isPortrait && (os === "Android" || os === "iPhone")) {
      // Note: INVBG-152にてuserAgentを修正したが、カメラ領域に関してはコードを精査してから修正する必要があるため、今までと同様の状態になるようにiPadはこの分岐に含めていない
      return {
        width: regionSize.height,
        height: regionSize.width,
      };
    }

    return {
      width: regionSize.width,
      height: regionSize.height,
    };
  };

  // ウィンドウリサイズ時処理
  const handleWindowResize = () => {
    setWindowSize(getModalDimensions());
    setRegionSize(getRegionSize());
    setCameraSize(calcCameraSize());
  };

  // 現在のModalサイズ
  const [windowSize, setWindowSize] = useState({ width: 0, height: 0 });
  console.log("windowSize:", windowSize);

  // 現在のカメラ領域サイズ
  const [regionSize, setRegionSize] = useState({ width: 0, height: 0 });
  console.log("regionSize:", regionSize);

  // カメラサイズ
  const [cameraSize, setCameraSize] = useState({ width: 1000, height: 1000 });
  console.log("cameraSize:", cameraSize);

  // WebCam
  const webcamRef = useRef<Webcam>(null);

  const { setError } = useContext(ErrorDialogContext);

  // 撮影ボタン押下時処理
  const wrapperOnShoot = () => {
    const onShoot = async () => {
      if (webcamRef.current === null) {
        return;
      }

      try {
        // キャプチャ取得
        const imageSrc = webcamRef.current.getScreenshot();

        // 保存
        if (imageSrc) {
          addImageList(imageSrc);
          changeStatus(OcrCameraAggregateMode.Confirm);
        }
        else {
          throw new MoneyLinkException({ error: ClientSystemError.UnLoadCamera });
        }
        // props.changeStatus(Mode.MODE_CONFIRM);
      } catch (error) {
        console.error(error);
        throw MoneyLinkException.wrapCallback(error, () => {});
      }
    };
    return { onShoot: errorHandledAsync(onShoot, setError) };
  };

  const { onShoot } = wrapperOnShoot();

  // 描画完了時
  useEffect(() => {
    window.addEventListener("resize", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  });

  useEffect(() => {
    handleWindowResize();
  }, []);

  const isPC = useBreakPoint().isPC;

  // カメラサイズ調整
  const cameraWrapperRef = useRef<HTMLDivElement>(null);
  const [aspectRatio, setAspectRatio] = useState<number>();

  useEffect(() => {
    const update = () => {
      const el = cameraWrapperRef.current;
      if (!el) return;
      setAspectRatio(el.clientWidth / el.clientHeight);
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  return (
    <>
      <div 
        ref={cameraWrapperRef}
        className="camera-with-frame">
        <div className="photo-region">
          {/* Webカメラ */}
          <Webcam
            ref={webcamRef}
            audio={false}
            screenshotFormat="image/jpeg"
            videoConstraints={{
              facingMode: "environment",
              ...(aspectRatio && { aspectRatio }),
            }}
          />
        </div>
      </div>

      {/* ボタン領域 */}
      <nav className="shutter-button-region">
        {isPC ? (
          // PC用
          <BaseRoundButton
            buttonText="撮影"
            onClick={onShoot}
          />
        ) : (
          // モバイル用(インボイス王はカメラっぽいデザインのボタンイラストをここに入れている)
          <BaseRoundButton
            buttonText="撮影"
            onClick={onShoot}
          />
        )}
      </nav>
    </>
  );
};
